/**
 * Dropping the old-format copy of maps in schema v2
 * (docs/plans/2026-10-13-drop-schema-v1.md). A converted map keeps the
 * copy (`nodes`) its conversion left for two weeks; then, when a member who
 * can write opens it or the background pass reaches it
 * (background-conversion.js), this device:
 *
 * 1. saves a backup of the whole map (engine/sync/migration-backups.js); no
 *    backup, no drop;
 * 2. drops the copy (YjsDocManager.dropOldCopy: late changes folded in,
 *    nothing deleted unless the map accounts for every node of the copy,
 *    one transaction);
 * 3. once the server has the change, compacts the map so the server stores
 *    it smaller, and notes the compaction confirmed (until then the
 *    background pass compacts it again, and its backup stays).
 *
 * A crash at any step leaves the map as it was or with the step done; the
 * next open or pass goes on from there.
 */
import * as Y from "yjs";
import { getPref, setPref, removePref } from "../storage/index.js";
import { whenConfirmed } from "./unsynced-changes.js";
import { getShareRole } from "./share-state.js";
import * as StorageHealth from "./storage-health.js";
import { readTree } from "./map-format.js";
import {
  saveOldCopyBackup, markOldCopyCompacted, forgetOldCopyBackups, readOldCopyBackup, listOldCopyBackups,
} from "./migration-backups.js";

const DAY_MS = 24 * 60 * 60 * 1000;
/** How long a converted map keeps its old copy (decided 2026-10-13). */
let dropAfterMs = 14 * DAY_MS;
// Bound (liveness, not timing): how long the drop may wait for the server
// before its compaction is left for the next background pass.
const CONFIRM_BOUND_MS = 120000;
// This device's note of when each map's copy is due (the background pass
// skips the map until then instead of opening it every session).
const DUE_PREF = "orbital-old-copy-due";

/**
 * Checks only: how long a converted map keeps its copy (0: dropped at
 * once). The due dates noted with the previous delay are forgotten.
 */
export function setOldCopyDelay(ms) {
  dropAfterMs = ms;
  try { removePref(DUE_PREF); } catch { /* nothing noted */ }
}

const within = (promise, ms) => Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(false), ms))]);

/** docId → when its copy is due, as noted on this device. */
export function oldCopyDueDates() {
  try {
    const saved = JSON.parse(getPref(DUE_PREF) || "{}");
    return saved && typeof saved === "object" ? saved : {};
  } catch {
    return {};
  }
}

function noteDue(docId, dueAt) {
  const due = oldCopyDueDates();
  if (dueAt === null) delete due[docId];
  else due[docId] = dueAt;
  try { setPref(DUE_PREF, JSON.stringify(due)); } catch { /* the pass opens the map again: harmless */ }
}

/**
 * Drop one map's old copy if it is due: the backup first, then the drop.
 * @param {string} docId
 * @param {import('./yjs-doc.js').YjsDocManager} manager - the map, synced
 * @param {{now?: number, stillOpen?: () => boolean}} [options] - stillOpen:
 *   false once the map was closed during the backup (nothing is dropped)
 * @returns {Promise<{dropped: boolean, reason?: string, count?: number, dueAt?: number}>}
 */
export async function dropOldCopyIfDue(docId, manager, { now = Date.now(), stillOpen = () => true } = {}) {
  if (!manager?.hasOldCopy?.()) return { dropped: false, reason: "none" };
  if (getShareRole(docId) === "viewer") return { dropped: false, reason: "viewer" };
  const convertedAt = manager.convertedAt();
  if (convertedAt === null) return { dropped: false, reason: "no-conversion-record" };
  const dueAt = convertedAt + dropAfterMs;
  if (now < dueAt) {
    noteDue(docId, dueAt);
    return { dropped: false, reason: "young", dueAt };
  }
  if (!StorageHealth.isHealthy()) return { dropped: false, reason: "storage" };
  if (!(await saveOldCopyBackup(docId, manager.createBackup(), now))) return { dropped: false, reason: "backup" };
  if (!stillOpen()) return { dropped: false, reason: "closed" };
  const result = manager.dropOldCopy();
  if (result.dropped) noteDue(docId, null);
  else await forgetOldCopyBackups([docId]); // nothing dropped: the backup is not needed
  return result;
}

/**
 * After a drop (or for a backup whose compaction is not confirmed): once
 * the server has every change of this map, compact it; note it confirmed.
 * @param {string} docId
 * @param {() => Promise<boolean>} compact - true once the server confirmed it
 * @returns {Promise<boolean>}
 */
export async function compactAfterDrop(docId, compact) {
  const sent = await within(whenConfirmed(docId).then(() => true), CONFIRM_BOUND_MS);
  if (!sent) return false;
  if (!(await compact())) return false;
  await markOldCopyCompacted(docId);
  return true;
}

/**
 * The open map, once synced (sync-orchestrator's post-sync step, after the
 * conversion): drop its old copy if due, then compact; or only compact, if
 * a drop's compaction is owed (the app stopped before it was confirmed).
 * @param {Object} bridge - CRDTBridge
 * @param {{online: boolean}} where - online: false for a map kept on this device only
 */
export async function dropOpenMapOldCopy(bridge, { online }) {
  const docId = bridge?.getCurrentDocId?.();
  const manager = bridge?.getDocManager?.();
  if (!docId || !manager) return null;
  const stillOpen = () => bridge.getCurrentDocId?.() === docId && bridge.getDocManager?.() === manager;
  const result = await dropOldCopyIfDue(docId, manager, { stillOpen });
  const owed = !result.dropped && (await listOldCopyBackups())[docId]?.compacted === false;
  if (!result.dropped && !owed) return result;
  if (!online) {
    await markOldCopyCompacted(docId); // nothing on a server to make smaller
    return result;
  }
  if (result.dropped) bridge.reportMapFormat?.();
  await compactAfterDrop(docId, async () => stillOpen() && !!(await bridge.forceCompaction?.("old-copy-dropped")));
  return result;
}

/**
 * Recovery (console: OrbitalCore.recovery.saveOldCopy, which saves it): the
 * old copy a backup holds as a map export file, to import as a new map and
 * compare. Notes are not part of the copy (they stay in the map), so
 * imported nodes keep only a plain-text note if the copy had one.
 * @returns {Promise<{blob: Blob, filename: string, nodes: number}>}
 */
export async function oldCopyFromBackup(docId) {
  const bytes = await readOldCopyBackup(docId);
  if (!bytes) throw new Error(`No backup of the old copy of ${docId} on this device`);
  const doc = new Y.Doc();
  try {
    Y.applyUpdate(doc, bytes);
    doc.getMap("metadata").delete("schemaVersion"); // this copy only: read the old copy
    let nodes = 0;
    const data = readTree(doc, (flat) => {
      nodes++;
      const { childIds: _c, parentId: _p, order: _o, notesFormat: _f, ...fields } = flat;
      return { ...fields, id: flat.id, name: flat.name || "" };
    });
    if (!data) throw new Error(`The backup of ${docId} holds no old copy`);
    const name = `${data.name} (before cleanup)`;
    const exportData = { version: 1, name, data, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    return { blob, filename: `old-copy-${docId}.json`, nodes };
  } finally {
    doc.destroy();
  }
}
